import React, { useEffect } from "react";
import AOS from "aos";
import { motion } from "framer-motion";
import {
    Download,
    ShieldCheck,
    Scale,
    Users,
    Target,
    BookOpen,
    Building2,
} from "lucide-react";
import SEO from "../components/SEO";

const downloads = [
    {
        title: "Ethics Fee Structure",
        description: "Download the committee fee structure in PDF format.",
        href: "/assets/files/fee_struture.pdf",
        fileType: "PDF",
    },
    {
        title: "Committee Organogram",
        description: "Download the governance and reporting structure in Word format.",
        href: "/assets/files/organogram.docx",
        fileType: "DOCX",
    },
    {
        title: "Strategic and Operational Plan",
        description: "Download the wider institutional strategy and operational plan.",
        href: "/assets/files/sop.docx",
        fileType: "DOCX",
    },
];

const governanceRoles = [
    "Vice Chancellor: Dr J Malan",
    "Deputy Vice Chancellor, Academic: Prof Dr FM Phiri",
    "Deputy Vice Chancellor Research: Dr D Mainde",
    "Quality Assurance: Dr D Mainde",
    "Principal & Registrar: Mr I Kumwenda",
    "Chief Librarian: Mr T G Ndhlovu",
    "Chief Financial Officer: Ms C Chisenga",
];

const values = [
    "We are honest, even when it means taking the long way around.",
    "We pursue the truth, even when it is hard.",
    "We respect our leaders and obey the law.",
    "We are sincere in our commitments.",
    "We are dedicated to quality.",
    "We strive to be accurate, timeous and exceed our students' expectations.",
];

const priorities = [
    "Sustainability and growth.",
    "Continuous improvement in quality of educational content and delivery.",
    "Continuous improvement of accessibility and service delivery to students.",
];

const organogram = {
    leader: {
        title: "Vice Chancellor",
        name: "Dr J Malan",
    },
    assistant: {
        title: "Quality Assurance",
        name: "Dr D Mainde",
    },
    branches: [
        {
            title: "Deputy Vice Chancellor, Academic",
            name: "Prof Dr FM Phiri",
        },
        {
            title: "Deputy Vice Chancellor Research",
            name: "Dr D Mainde",
        },
        {
            title: "Principal & Registrar",
            name: "Mr I Kumwenda",
        },
        {
            title: "Chief Librarian",
            name: "Mr T G Ndhlovu",
        },
        {
            title: "Chief Financial Officer",
            name: "Ms C Chisenga",
        },
    ],
};

const EthicsCommitteePage = () => {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    useEffect(() => {
        try {
            if (typeof window !== "undefined" && window?.scrollTo) {
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
            }
        } catch (error) {
            try {
                window.scrollTo(0, 0);
            } catch (fallbackError) {
                // Ignore environments without window support.
            }
        }
    }, []);

    return (
        <section className="bg-gray-100 min-h-screen pt-12">
            <SEO
                title="Ethics Committee - Africa Research University"
                description="Governance, values, and downloadable ethics committee documents for Africa Research University."
                url="/ethics-committee"
                breadcrumbs={[
                    { name: "Home", url: "https://africaresearchuniversity.com" },
                    { name: "About", url: "https://africaresearchuniversity.com/about" },
                    { name: "Ethics Committee", url: "https://africaresearchuniversity.com/ethics-committee" },
                ]}
            />

            <div className="container mx-auto px-6 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="max-w-6xl mx-auto bg-white rounded-2xl shadow-sm border p-6 sm:p-10"
                >
                    <div className="text-center max-w-3xl mx-auto mb-12" data-aos="fade-up">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-4 py-1 text-sm font-semibold text-green-700 mb-4">
                            Research Governance
                        </span>
                        <h1 className="text-3xl sm:text-4xl font-bold text-green-700 mb-4">
                            Ethics Committee
                        </h1>
                        <p className="text-gray-600 leading-relaxed">
                            The Africa Research University Ethics Committee is anchored in the
                            university&apos;s commitment to truth, quality, accountability, and
                            service. This page brings together the core governance context and
                            supporting documents that guide ethical oversight within the wider
                            university framework.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 mb-12">
                        <div className="bg-green-50 border border-green-100 rounded-2xl p-6" data-aos="fade-up">
                            <ShieldCheck className="text-green-700 mb-4" size={32} />
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">Ethical Foundation</h2>
                            <p className="text-gray-700 text-sm leading-relaxed">
                                ARU states that its ethics are based on Biblical values and that
                                it is committed to honesty, truth, sincerity, quality, and lawful
                                conduct.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6" data-aos="fade-up" data-aos-delay="100">
                            <Scale className="text-blue-700 mb-4" size={32} />
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">Compliance Context</h2>
                            <p className="text-gray-700 text-sm leading-relaxed">
                                The university&apos;s strategic plan notes alignment with the Higher
                                Education Act of 2013, strengthened governance, and ongoing
                                compliance through council, quality assurance, and reporting lines.
                            </p>
                        </div>

                        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6" data-aos="fade-up" data-aos-delay="200">
                            <Users className="text-amber-700 mb-4" size={32} />
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">Service Focus</h2>
                            <p className="text-gray-700 text-sm leading-relaxed">
                                ARU&apos;s wider operational direction emphasizes accessible service,
                                quality delivery, improved systems, and responsive support for
                                students, staff, and researchers.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 mb-12">
                        <div className="bg-gray-50 rounded-2xl border p-6" data-aos="fade-up">
                            <div className="flex items-center gap-3 mb-4">
                                <Target className="text-green-700" size={24} />
                                <h2 className="text-2xl font-semibold text-green-700">Vision and Purpose</h2>
                            </div>
                            <p className="text-gray-700 mb-4 leading-relaxed">
                                ARU&apos;s vision is to unlock the potential of postgraduate and
                                undergraduate learners in Africa through accessible,
                                industry-relevant education that strengthens employability and
                                drives career growth.
                            </p>
                            <p className="text-gray-700 mb-4 leading-relaxed">
                                Its mission is to support students to achieve their academic and
                                personal career potential through affordable and excellent services.
                            </p>
                            <p className="text-gray-700 leading-relaxed">
                                Within that institutional purpose, ethical review supports
                                responsible research, sound decision-making, and trust in academic
                                and professional practice.
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-2xl border p-6" data-aos="fade-up" data-aos-delay="100">
                            <div className="flex items-center gap-3 mb-4">
                                <BookOpen className="text-green-700" size={24} />
                                <h2 className="text-2xl font-semibold text-green-700">Core Values</h2>
                            </div>
                            <div className="space-y-3">
                                {values.map((value) => (
                                    <div key={value} className="flex items-start gap-3">
                                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-green-600 flex-shrink-0" />
                                        <p className="text-gray-700">{value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 mb-12">
                        <div className="bg-white rounded-2xl border p-6" data-aos="fade-up">
                            <div className="flex items-center gap-3 mb-5">
                                <Building2 className="text-green-700" size={24} />
                                <h2 className="text-2xl font-semibold text-green-700">Governance Structure</h2>
                            </div>
                            <p className="text-gray-700 mb-6 leading-relaxed">
                                The organogram places ethics-related oversight within the
                                university&apos;s broader leadership, research, academic, quality
                                assurance, registry, library, and finance structures.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {governanceRoles.map((role) => (
                                    <div key={role} className="rounded-xl bg-gray-50 border px-4 py-3 text-gray-800">
                                        {role}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border p-6" data-aos="fade-up" data-aos-delay="100">
                            <h2 className="text-2xl font-semibold text-green-700 mb-5">Strategic Alignment</h2>
                            <div className="space-y-4 mb-6">
                                {priorities.map((priority) => (
                                    <div key={priority} className="rounded-xl bg-green-50 border border-green-100 px-4 py-3 text-gray-700">
                                        {priority}
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                The university also highlights stronger reporting channels,
                                improved feedback loops, digital systems, and continuous quality
                                improvement as part of its operational direction.
                            </p>
                        </div>
                    </div>

                    <div className="mb-12" data-aos="fade-up">
                        <h2 className="text-2xl font-semibold text-green-700 mb-6">Organogram Preview</h2>
                        <div className="rounded-2xl border bg-gradient-to-b from-white to-green-50/60 p-6 sm:p-8">
                            <div className="mx-auto flex max-w-5xl flex-col items-center">
                                <div className="rounded-2xl border border-green-200 bg-white px-6 py-4 text-center shadow-sm">
                                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-green-700">
                                        {organogram.leader.title}
                                    </p>
                                    <p className="mt-2 text-lg font-bold text-gray-900">
                                        {organogram.leader.name}
                                    </p>
                                </div>

                                <div className="relative flex w-full flex-col items-center pt-8">
                                    <div className="absolute left-1/2 top-0 hidden h-8 w-px -translate-x-1/2 bg-green-300 xl:block" />

                                    <div className="relative flex w-full justify-center">
                                        <div className="absolute left-1/2 top-1/2 hidden h-px w-28 -translate-x-full -translate-y-1/2 bg-green-300 xl:block" />

                                        <div className="absolute left-1/2 top-1/2 hidden -translate-x-[calc(100%+7rem)] -translate-y-1/2 xl:block">
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center shadow-sm">
                                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                                                    {organogram.assistant.title}
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    {organogram.assistant.name}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center shadow-sm xl:hidden">
                                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                                                Assistant
                                            </p>
                                            <p className="mt-2 text-base font-semibold text-gray-900">
                                                {organogram.assistant.title}
                                            </p>
                                            <p className="text-sm text-gray-700">
                                                {organogram.assistant.name}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="hidden h-8 w-px bg-green-300 xl:block" />

                                    <div className="relative w-full pt-0 xl:pt-8">
                                        <div className="absolute left-1/2 top-0 hidden h-8 w-px -translate-x-1/2 bg-green-300 xl:block" />
                                        <div className="absolute left-1/2 top-8 hidden h-px w-[80%] -translate-x-1/2 bg-green-300 xl:block" />

                                        <div className="grid w-full gap-4 sm:grid-cols-2 xl:grid-cols-5">
                                            {organogram.branches.map((branch) => (
                                                <div key={branch.title} className="flex flex-col items-center">
                                                    <div className="hidden h-8 w-px bg-green-300 xl:block" />
                                                    <div className="w-full rounded-2xl border border-green-100 bg-white px-4 py-4 text-center shadow-sm">
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            {branch.title}
                                                        </p>
                                                        <p className="mt-2 text-sm text-gray-600">
                                                            {branch.name}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div data-aos="fade-up">
                        <h2 className="text-2xl font-semibold text-green-700 mb-6">Downloads</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {downloads.map((item, index) => (
                                <div
                                    key={item.title}
                                    className="rounded-2xl border bg-gray-50 p-6"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="flex items-center justify-between mb-4 gap-3">
                                        <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                                        <span className="text-xs font-semibold uppercase tracking-wide text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                            {item.fileType}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-5">
                                        {item.description}
                                    </p>
                                    <a
                                        href={item.href}
                                        download
                                        className="inline-flex items-center gap-2 bg-green-600 text-white font-semibold px-5 py-3 rounded-full hover:bg-green-700 transition duration-300"
                                    >
                                        <Download size={18} />
                                        Download File
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default EthicsCommitteePage;
